import React from "react";
import {
  AspectRatio,
  Center,
  HStack,
  Icon,
  Pressable,
  Spinner,
  Text,
} from "native-base";
import { Feather } from "@expo/vector-icons";
import PlaceholderGradient from "./PlaceholderGradient";

type Props = {
  onPress: () => void;
  // Omitted by the small grid tiles, which show the plus on its own.
  label?: string;
  isLoading?: boolean;
  // Tighter pill for tiles that only get a fraction of the row width.
  compact?: boolean;
  // Matches the photo tiles by default; full-width cards pass a wider ratio so
  // they do not turn into a huge empty block.
  ratio?: number;
};

export default function PhotoButton({
  onPress,
  label,
  isLoading,
  compact,
  ratio = 4 / 3,
}: Props) {
  // Without a label the pill collapses to a circle, so the horizontal padding
  // has to match the vertical one instead of leaving room for text.
  const hasLabel = !!label;

  return (
    <AspectRatio ratio={ratio} flex={1}>
      <Pressable
        bgColor="white"
        borderRadius="lg"
        overflow="hidden"
        onPress={onPress}
        _pressed={{ opacity: 0.5 }}
        w="full"
        h="full"
      >
        <PlaceholderGradient />

        <Center h="full" w="full" p={compact ? 2 : 3}>
          <HStack
            space={compact ? 1 : 1.5}
            alignItems="center"
            bg="white"
            borderRadius="full"
            px={hasLabel ? (compact ? 3 : 4) : 2}
            py={2}
            maxW="full"
          >
            {isLoading ? (
              <Spinner color="ink.900" size="sm" />
            ) : (
              <Icon
                as={Feather}
                name="plus"
                color="ink.900"
                size={hasLabel ? (compact ? "xs" : "sm") : "md"}
              />
            )}

            {label ? (
              <Text
                color="ink.900"
                fontSize={compact ? "xs" : "sm"}
                fontWeight="medium"
                textAlign="center"
                flexShrink={1}
                // Labels are single nouns by design; clamp anyway so a longer
                // one ellipsizes instead of ballooning the pill on the narrow
                // half-width cards.
                noOfLines={1}
              >
                {label}
              </Text>
            ) : null}
          </HStack>
        </Center>
      </Pressable>
    </AspectRatio>
  );
}
